<script setup lang="ts">
import {computed, ref} from 'vue'
import {useStore} from 'vuex'

import {formatTime} from '@/utils/datetime'
import { MoreHorizontal } from 'lucide-vue-next'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { useCheckOrgAccess } from '@/composables/useCheckAccess'

import type { OrgMember } from '@/types'
import MembersListEditDialog from './MembersListEditDialog.vue'


const store = useStore()
const settings = computed(() => store.getters['user/getUserSettings'])
const org = computed(() => store.getters['org/getCurrent'])
const props = defineProps<{
  member: OrgMember,
  projectOnly?: boolean
}>()

const createdAt = computed(() => formatTime(props.member.create_dttm, settings.value.tz, 'D MMMM YYYY'))
const editOpen = ref(false)

const remove = async (ev: any) => {
  await store.dispatch('org/deleteMember', {
    orgId: org.value.organization_id,
    orgMemberId: props.member.organization_member_id
  })
}

</script>

<template>
  <MembersListEditDialog
    v-if="!projectOnly && useCheckOrgAccess(org, 'org_member', ['update'])"
    :member="member"
    :org="org"
    :open="editOpen"
    @update:open="editOpen = $event"
  />

  <DropdownMenu>
    <DropdownMenuTrigger as-child>
      <Button variant="ghost" class="w-8 h-8 p-0">
        <span class="sr-only">Open</span>
        <MoreHorizontal class="w-4 h-4" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent v-if="projectOnly" align="end" class="max-h-64 overflow-y-auto">
      <DropdownMenuLabel>Projects</DropdownMenuLabel>
      <DropdownMenuSeparator />
      <template v-if="member.projects?.length">
        <DropdownMenuItem v-for="p in member.projects" :key="p.project_id" as-child>
          <router-link :to="{ name: 'ProjectMembers', params: { org_id: org.organization_id, project_id: p.project_id } }">
            {{ p.project_name }}
          </router-link>
        </DropdownMenuItem>
      </template>
      <DropdownMenuItem v-else disabled>No projects</DropdownMenuItem>
    </DropdownMenuContent>
    <DropdownMenuContent v-else align="end">
      <DropdownMenuItem>Member since: {{createdAt}}</DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem
        v-if="useCheckOrgAccess(org, 'org_member', ['update'])"
        @click="editOpen = true">Edit</DropdownMenuItem>
      <DropdownMenuItem
        v-if="useCheckOrgAccess(org, 'org_member', ['delete'])"
        @click="remove"
        class="text-red-500">Delete</DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
</template>
